import { Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, ActivatedRouteSnapshot, Router, Routes, RouterLink } from '@angular/router'
import { NgClass, NgStyle, KeyValuePipe } from '@angular/common'
import { TranslateService } from '@ngx-translate/core'

import { ClientSession, Workstation } from 'elements-sdk'
import { APIService } from 'api/services/api.service'
import { MessageBoxService, NotifyService } from 'common/util'
import { SocketService } from 'common/util/services/socket.service'
import { PageTitleComponent } from 'common/util/components/pageTitle.component'
import { TranslateDirective } from 'common/locale/directives/translate.directive'
import { AvatarComponent } from 'common/util/components/avatar.component'
import { BadgeComponent } from 'common/util.badge/component/badge.component'
import { EmptyStateComponent } from 'common/util/components/emptyState.component'
import { PermissionEnableDirective } from 'common/util/directives/permissionEnable.directive'
import { NewBadgeComponent } from 'common/util.releaseNotes/components/newBadge.component'
import { FloatingToolbarComponent } from 'common/util/components/floatingToolbar.component'
import { PermissionShowDirective } from 'common/util/directives/permissionShow.directive'
import { SaveButtonComponent } from 'common/util/components/saveButton.component'
import { TimeAgoPipe } from 'common/luxonDateTime/pipes/timeAgo.pipe'
import { UtcPipe } from 'common/luxonDateTime/pipes/utc.pipe'
import { TranslatePipe } from 'common/locale'
import { permissionGuard } from 'main/services/identity.service'
import { StorageConstants } from 'storage/constants'

@Component({
  selector: 'workstation-view',
  templateUrl: './workstationView.component.html',
  imports: [
    PageTitleComponent,
    TranslateDirective,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    AvatarComponent,
    NgClass,
    NgStyle,
    BadgeComponent,
    EmptyStateComponent,
    PermissionEnableDirective,
    NewBadgeComponent,
    FloatingToolbarComponent,
    PermissionShowDirective,
    SaveButtonComponent,
    KeyValuePipe,
    TimeAgoPipe,
    UtcPipe,
    TranslatePipe,
  ],
})
export class WorkstationViewComponent {
  workstation: Workstation
  clientSessions: readonly ClientSession[]

  form: FormGroup

  constructor (
    private fb: FormBuilder,
    private readonly api: APIService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly socket: SocketService,
    private readonly notify: NotifyService,
    private readonly messagebox: MessageBoxService,
    private readonly storageConstants: StorageConstants,
    private readonly translate: TranslateService,

  ) {
    this.socket.subject('mounted-workspaces:updated')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.reloadSessions())
  }

  async ngOnInit (): Promise<void> {
    this.workstation = this.route.snapshot.data['workstation']
    this.clientSessions = this.route.snapshot.data['clientSessions']
    this.form = this.fb.group({
      workstationName: [this.workstation.name, [Validators.maxLength(255)]],
    })
  }

  get workstationName (): string {
    return this.form.value.workstationName?.trim()
  }

  async reloadSessions () {
    this.clientSessions = await this.api.main.getAllClientSessions({ workstation: this.workstation.id })
  }

  getProtocolName (id: string | null): string {
    if (!id) {
      return ''
    }
    return this.storageConstants.protocolNames[id] ?? id ?? ''
  }

  save = async () => {
    this.workstation.name = this.workstationName
    await this.api.main.updateWorkstation({ id: this.workstation.id, workstationUpdate: this.workstation })
    this.notify.success(this.translate.instant('Saved'))
  }

  async delete () {
    try {
      await this.messagebox.show({
        text: this.translate.instant('Delete this workstation entry? It will be automatically recreated if the workstation connects again.'),
        title: this.translate.instant('Delete'),
        positive: this.translate.instant('Delete'),
        negative: this.translate.instant('Keep'),
        danger: true,
      })
      try {
        await this.api.main.deleteWorkstation({ id: this.workstation.id })
        this.notify.success(this.translate.instant('Deleted'))
        this.router.navigateByUrl('/workstation')
      } catch (error) {
        this.notify.errorFromResponse(this.translate.instant('Error occurred while deleting'), error)
      }
    } catch {
      return
    }
  }

  static readonly ROUTES: Routes = [
    {
      path: ':id',
      component: WorkstationViewComponent,
      canActivate: [permissionGuard('workstations:view')],
      resolve: {
        workstation: (route: ActivatedRouteSnapshot) => inject(APIService).main.getWorkstation({ id: route.paramMap.get('id')! }),
        clientSessions: (route: ActivatedRouteSnapshot) => inject(APIService).main.getAllClientSessions({ workstation: route.paramMap.get('id')! }),
      },
    },
  ]
}
